import { css } from '@emotion/react'
import { Cell, FormControl, Grid, Theme, Tooltip, useTheme } from 'bold-ui'
import { CheckboxField } from 'components/form'
import { usePecField } from 'components/form/core/hooks/useField'
import { LotacaoAndEstagioSelectField } from 'components/form/field/select/LotacaoAndEstagioSelectField/LotacaoAndEstagioSelectField'
import { TipoParticipacaoAtendimentoSelectField } from 'components/form/field/select/TipoParticipacaoAtendimentoSelectField'
import { useFlags } from 'config/useFlagsContext'
import { MetaPath } from 'util/metaPath'
import { OBSERVACAO_DISABLE_ATENDIMENTO_COMPARTILHADO } from 'view/atendimentos/atendimento-individual/atendimento-observacao/model-atendObservacao'

import { FinalizacaoAtendimentoFormModel } from '../FinalizacaoAtendimentoForm'
import { ObrigatoriedadesParticipacao } from '../util'

export interface AtendimentoCompartilhadoFieldGroupProps {
  name: MetaPath<FinalizacaoAtendimentoFormModel>
  disabled: boolean
  profissionaisIdsInvalidosAtendimentoCompartilhado: ID[]
  obrigatoriedadesParticipacao: ObrigatoriedadesParticipacao
}

// TODO @CODEBENDERS - Criar um componente genérico utilizando <fieldset> e <legend> (#18472)
export function AtendimentoCompartilhadoFieldGroup(props: Readonly<AtendimentoCompartilhadoFieldGroupProps>) {
  const {
    name,
    disabled,
    profissionaisIdsInvalidosAtendimentoCompartilhado,
    obrigatoriedadesParticipacao: { isParticipacaoAmbos, isParticipacaoProfissional },
  } = props

  const {
    input: { value: atendimentoCompartilhadoLotacao },
  } = usePecField({ name: name.atendimentoCompartilhadoLotacao })
  const {
    input: { value: isProfissionalNaoListado },
  } = usePecField({ name: name.isProfissionalNaoListado })

  const { PILOTO_PARTICIPACAO_OBRIGATORIA_TELECONSULTA_ENABLED } = useFlags()
  const isProfissionalDisabled = PILOTO_PARTICIPACAO_OBRIGATORIA_TELECONSULTA_ENABLED && isProfissionalNaoListado
  const isFormaParticipacaoEnabled = !!atendimentoCompartilhadoLotacao || isProfissionalDisabled

  const theme = useTheme()
  const styles = createStyles(theme)

  return (
    <Tooltip text={disabled && OBSERVACAO_DISABLE_ATENDIMENTO_COMPARTILHADO}>
      <div css={styles.container}>
        <FormControl
          label='Atendimento compartilhado'
          style={styles.title}
          required={isParticipacaoAmbos || isParticipacaoProfissional}
        />
        <Grid alignItems='center'>
          <Cell size={12}>
            <LotacaoAndEstagioSelectField
              excludeProfissionaisIds={profissionaisIdsInvalidosAtendimentoCompartilhado}
              name={name.atendimentoCompartilhadoLotacao}
              label='Profissional'
              loadItemsOnOpen={false}
              disabled={disabled || isProfissionalDisabled}
            />
            {PILOTO_PARTICIPACAO_OBRIGATORIA_TELECONSULTA_ENABLED && (
              <CheckboxField
                name={name.isProfissionalNaoListado}
                label='Profissional não listado no sistema'
                style={styles.checkbox}
                disabled={disabled}
              />
            )}
          </Cell>
          <Cell size={12}>
            <TipoParticipacaoAtendimentoSelectField
              label='Forma de participação'
              name={name.tipoParticipacaoProfissionalConvidado}
              disabled={disabled || !isFormaParticipacaoEnabled}
              required={PILOTO_PARTICIPACAO_OBRIGATORIA_TELECONSULTA_ENABLED && isFormaParticipacaoEnabled}
            />
          </Cell>
        </Grid>
      </div>
    </Tooltip>
  )
}

const createStyles = (theme: Theme) => ({
  container: css`
    position: relative;
    margin-top: 0.5rem;
    padding: 1rem;
    border: 1px solid ${theme.pallete.gray.c80};
  `,
  title: css`
    font-weight: bold;
    position: absolute;
    margin-left: -0.5rem;
    top: -0.7rem;
    padding: 0 0.5rem;
    background-color: ${theme.pallete.surface.main};
  `,
  checkbox: css`
    font-weight: bold;
    position: relative;
    margin-left: -0.7rem;
    top: 0.5rem;
    padding: 0 0.5rem;
    background-color: ${theme.pallete.surface.main};
  `,
})
